import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import {
    type ActiveFacility,
    activeFacilityInjectionKey
} from '~/composables/use-active-facility'
import { useShowAlert } from '~/composables/use-show-alert'
import type { InjectionKey, Ref } from 'vue'
import { inject } from 'vue'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { version } from '~/composables/meta'
import type { AwesomeGraphQLClient } from 'awesome-graphql-client'
import { GraphQLRequestError } from 'awesome-graphql-client'
import { useState } from '#imports'
import { useMobileAppData } from '~/composables/mobile/mobile-app-data'
import { CSRF_TOKEN_STATE_KEY } from '~/composables/use-state-constants'

export const graphqlClientInjectionKey: InjectionKey<AwesomeGraphQLClient> =
    Symbol('graphqlClientInjectionKey')
export type CsrfTokenBootstrapper = () => Promise<string | null>
export const csrfTokenBootstrapperInjectionKey: InjectionKey<CsrfTokenBootstrapper> =
    Symbol('csrfTokenBootstrapperInjectionKey')

export class UserReportableError extends Error {
    // Keep structured recovery instructions after the shared alert is dismissed,
    // so callers never have to infer the cause from a translated message.
    constructor(public readonly extensions: Record<string, unknown>) {
        super()
    }
}
export class ObsoleteQueryError extends Error {}

/**
 * @param orgLevel Provide as true to execute a query with an active facility (usually to determine the active facility)
 */
export function useRpQuery({
    orgLevel,
    silent = false
}: { orgLevel?: boolean; silent?: boolean } = {}) {
    const showAlert = useShowAlert()
    const csrfToken = useState<string | null>(CSRF_TOKEN_STATE_KEY, () => null)
    let activeFacility: Ref<ActiveFacility | undefined> | undefined = undefined
    if (!orgLevel) {
        activeFacility = inject(activeFacilityInjectionKey)
    }
    const client = inject(graphqlClientInjectionKey)
    if (client === undefined) {
        throw new Error('No graphql client provided')
    }
    const bootstrapCsrfToken = inject(csrfTokenBootstrapperInjectionKey)
    if (bootstrapCsrfToken === undefined) {
        throw new Error('No CSRF token bootstrapper provided')
    }
    const { isKiosk, isMobileApp } = useLayoutMode()
    const mobileAppData = useMobileAppData()

    async function clientQuery<T, V>(
        document: TypedDocumentNode<T, V>,
        variables?: V,
        requestHeaders?: Record<string, string>
    ): Promise<T> {
        if (client === undefined) {
            throw new Error('No graphql client provided')
        }

        return client.request(document, variables, {
            headers: requestHeaders
        })
    }

    async function runQuery<T, V>(
        document: TypedDocumentNode<T, V>,
        variables?: V
    ): Promise<T> {
        try {
            const requestHeaders: Record<string, string> = {
                'X-Redpoint-Hq-Client':
                    version.major + '.' + version.minor + '.' + version.patch
            }
            if (!isMobileApp.value) {
                // The native app authenticates with its bearer token, so it
                // neither needs nor establishes a cookie-bound CSRF token.
                const currentCsrfToken = await getCsrfToken(
                    csrfToken,
                    bootstrapCsrfToken
                )
                if (currentCsrfToken !== null) {
                    requestHeaders['X-CSRF-TOKEN'] = currentCsrfToken
                }
            }
            if (activeFacility !== undefined && activeFacility.value) {
                requestHeaders['RPHQ-Facility'] = activeFacility.value.id
            }
            if (isKiosk.value) {
                requestHeaders['RPHQ-Mode'] = 'KioskMode'
            }
            if (
                isMobileApp.value &&
                mobileAppData.value.bearerToken !== null &&
                // It should never be undefined, but it fails in Android simulator otherwise
                mobileAppData.value.bearerToken !== undefined
            ) {
                requestHeaders['Authorization'] =
                    'Bearer ' + mobileAppData.value.bearerToken
            }

            try {
                return await clientQuery(document, variables, requestHeaders)
            } catch (error) {
                if (
                    process.client &&
                    error instanceof GraphQLRequestError &&
                    error.response?.status === 419
                ) {
                    // Laravel rejects a CSRF mismatch before executing the
                    // mutation. Replace only the stale client token, then
                    // safely retry the request once with its current session.
                    csrfToken.value = null
                    const refreshedCsrfToken = await getCsrfToken(
                        csrfToken,
                        bootstrapCsrfToken
                    )

                    if (refreshedCsrfToken !== null) {
                        requestHeaders['X-CSRF-TOKEN'] = refreshedCsrfToken
                        // Mark only the one-time recovery request so Laravel can
                        // distinguish a customer-visible repeated failure from a
                        // normal stale-token refresh.
                        requestHeaders['X-CPX-CSRF-Retry'] = '1'

                        return await clientQuery(
                            document,
                            variables,
                            requestHeaders
                        )
                    }
                }

                throw error
            }
        } catch (error) {
            // Optional engagement reporting must retain authentication recovery without interrupting navigation with an alert.
            if (silent) {
                throw error
            }
            if (error instanceof GraphQLRequestError) {
                if (error.extensions !== undefined) {
                    if (error.extensions.validation !== undefined) {
                        throw error
                    } else if (
                        error.extensions.requiresClientRefresh === true
                    ) {
                        if (process.client) {
                            await showAlert(
                                error.extensions.title as string,
                                error.extensions.message as string,
                                false,
                                'Refresh'
                            )
                            refreshWithCacheBust()
                        }
                    } else if (
                        error.extensions.category === 'user-reportable'
                    ) {
                        if (process.client) {
                            await showAlert(
                                error.extensions.title as string,
                                error.extensions.message as string
                            )

                            throw new UserReportableError(error.extensions)
                        }
                    } else {
                        if (process.client) {
                            if (error.extensions.debugMessage !== undefined) {
                                await showAlert(
                                    'Service Notice',
                                    error.extensions.debugMessage as string
                                )
                            } else {
                                await showGenericError(error, showAlert)
                            }
                        }
                    }
                } else if (process.client) {
                    await showGenericError(error, showAlert)
                }
            }

            if (import.meta.env.DEV) {
                console.error(error, error.response)
            }
            throw error
        }
    }

    return <T, V>(
        document: TypedDocumentNode<T, V>,
        variables?: V
    ): Promise<T> => {
        return runQuery(document, variables)
    }
}

async function getCsrfToken(
    csrfToken: Ref<string | null>,
    bootstrapCsrfToken: CsrfTokenBootstrapper
): Promise<string | null> {
    if (csrfToken.value !== null) {
        return csrfToken.value
    }

    if (process.client) {
        csrfToken.value = await bootstrapCsrfToken()
    }

    // SSR seeds this state before GraphQL runs, while no-SSR mobile hosts
    // bootstrap on their first browser-side request.
    return csrfToken.value
}

function needsNewVersion(serverVersion: unknown) {
    if (
        typeof serverVersion === 'string' &&
        /^[0-9]+\.[0-9]+\.[0-9]+/.test(serverVersion)
    ) {
        const parts = serverVersion.split('.')
        const major = parseInt(parts[0])
        const minor = parseInt(parts[1])
        const patch = parseInt(parts[2])
        if (major > version.major) {
            return true
        } else if (major === version.major && minor > version.minor) {
            return true
        } else if (
            major === version.major &&
            minor === version.minor &&
            patch > version.patch
        ) {
            return true
        }
    } else {
        console.error('Invalid server version: ' + serverVersion)
    }

    return false
}

function refreshWithCacheBust() {
    // A unique document URL prevents a stale browser or intermediary HTML cache
    // from serving the old CPX entrypoint after the parity checkpoint fails.
    const url = new URL(window.location.href)
    url.searchParams.set('rphq-refresh', Date.now().toString())
    location.replace(url.toString())
}

async function showGenericError(
    error: GraphQLRequestError,
    showAlert: ReturnType<typeof useShowAlert>
) {
    if (error.response?.status === 419) {
        await showAlert(
            'Refresh Required',
            'This page has expired and could not be refreshed. Click "OK" to refresh and continue.'
        ).then(() => {
            location.reload()
        })
    } else if (
        needsNewVersion(error.response?.headers?.get('redpointhq-version'))
    ) {
        await showAlert(
            'New Version Available',
            'Whoops! It looks like you were viewing an old version of this page. Click "OK" to refresh.'
        ).then(() => {
            location.reload()
        })
    } else {
        const requestId =
            error.response?.headers?.get('request-id') ?? 'UNKNOWN'

        await showAlert(
            'Service Notice',
            'Hmmm.... Something was not quite right. Please try again and if the problem persists send the following Service ID to customer support.<br /><br />SERVICE ID: ' +
                requestId,
            true
        )
    }
}
